/**
 * Transform functions which takes in data as the input & return UI Config
 * Transform may be defined for a Question or an Answer as ultimately
 * the UI for both of them is represented by type 'RichComponentType[]'
 * Check './README.md' to know more.
 */

import { APP_DOES_NOT_SUPPORT_DR, STORED_EXTERNAL_TO_ATLASSIAN, STORED_IN_ATLASSIAN_N_DR_NOT_SUPPORTED, STORED_IN_ATLASSIAN_N_DR_SUPPORTED } from '@atlassian/mpac-api-client';
import { arrayToPoint } from "./components/BulletedPoints/BulletedPoints";
const yesNoText = bool => {
  if (typeof bool === 'undefined') {
    // TODO: Handle not applicable because the parent answer is no
    return null;
  }
  return bool ? [{
    type: 'text',
    text: 'Yes'
  }] : [{
    type: 'text',
    text: 'No'
  }];
};

// TODO: Add appropriate icons in each answer
export const endUserDataStoresAndProcessAnswer = ({
  dataAccessNStorage
}) => {
  const {
    appProcessEUDOutsideAtlassian,
    typesOfDataAccessed,
    appStoresEUDOutsideAtlassian,
    typesOfDataStored
  } = dataAccessNStorage;
  if (!appProcessEUDOutsideAtlassian && !appStoresEUDOutsideAtlassian) {
    return [{
      type: 'text',
      text: 'The app does not process or store End-User Data outside of Atlassian products and services.'
    }];
  }
  if (appProcessEUDOutsideAtlassian && !appStoresEUDOutsideAtlassian) {
    return [{
      type: 'bullet-points-list',
      bulletedList: [{
        label: 'The app processes but does not store the following types of End-User Data:',
        points: arrayToPoint(typesOfDataAccessed)
      }]
    }];
  }
  if (appStoresEUDOutsideAtlassian && !appProcessEUDOutsideAtlassian) {
    return [{
      type: 'bullet-points-list',
      bulletedList: [{
        label: 'The app does not process but stores the following types of End-User Data:',
        points: arrayToPoint(typesOfDataStored)
      }]
    }];
  }
  if (appStoresEUDOutsideAtlassian && appProcessEUDOutsideAtlassian) {
    return [{
      type: 'bullet-points-list',
      bulletedList: [{
        label: 'The app processes the following types of End-User Data:',
        points: arrayToPoint(typesOfDataAccessed)
      }, {
        label: 'The app stores the following types of End-User Data:',
        points: arrayToPoint(typesOfDataStored)
      }]
    }];
  }
  return null;
};
export const endUserDataSharedWithThirdPartyEntities = ({
  thirdPartyInformation
}) => {
  const {
    isEndUserDataShared
  } = thirdPartyInformation;
  if (!isEndUserDataShared) {
    return [{
      type: 'text',
      text: 'The app does not share End-User Data with third parties.'
    }];
  }
  if (thirdPartyInformation.thirdPartyDetails) {
    return [{
      type: 'bullet-points-list',
      bulletedList: thirdPartyInformation.thirdPartyDetails.map(({
        name,
        link,
        purpose,
        thirdPartyCountries
      }) => ({
        label: name,
        points: [{
          text: "Link: ".concat(link)
        }, {
          label: 'Location: ',
          isoCodes: thirdPartyCountries
        }, {
          text: "Purpose: ".concat(purpose)
        }]
      }))
    }];
  }
  return null;
};
export const dataResidencyOptions = ({
  dataResidency
}) => {
  const {
    isDataResidencySupported,
    countriesWhereEndUserDataStored
  } = dataResidency;
  if (isDataResidencySupported === STORED_EXTERNAL_TO_ATLASSIAN) {
    return [{
      type: 'text',
      text: 'The app supports data residency for in-scope End-User Data in the following locations:'
    }, {
      type: 'countries',
      isoCodes: countriesWhereEndUserDataStored
    }];
  }
  if (isDataResidencySupported === STORED_IN_ATLASSIAN_N_DR_SUPPORTED) {
    return [{
      type: 'rich-text',
      items: [{
        type: 'text',
        text: 'App stores End-User Data exclusively within Atlassian products and services which support data residency options, as outlined'
      }, {
        type: 'link',
        text: ' here.',
        href: 'https://support.atlassian.com/security-and-access-policies/docs/understand-data-residency/'
      }]
    }];
  }
  if (isDataResidencySupported === APP_DOES_NOT_SUPPORT_DR) {
    return [{
      type: 'text',
      text: 'App does not support data residency options.'
    }];
  }
  if (isDataResidencySupported === STORED_IN_ATLASSIAN_N_DR_NOT_SUPPORTED) {
    return [{
      type: 'rich-text',
      items: [{
        type: 'text',
        text: "App stores End-User Data exclusively within Atlassian products and services, which don't support data residency options yet due to Atlassian\u2019s current roadmap. (e.g. Atlassian\u2019s"
      }, {
        type: 'link',
        text: ' Forge ',
        href: 'https://developer.atlassian.com/platform/forge/'
      }, {
        type: 'text',
        text: 'platform).'
      }]
    }];
  }
  return null;
};
export const dataResidencyInScopeEndUserData = ({
  dataResidency
}) => {
  const {
    isDataResidencySupported,
    inScopeDataTypes
  } = dataResidency;
  if (isDataResidencySupported === STORED_EXTERNAL_TO_ATLASSIAN && inScopeDataTypes) {
    return [{
      type: 'bullet-points-list',
      bulletedList: [{
        label: 'App stores the following types of in-scope end-user data:',
        points: arrayToPoint(inScopeDataTypes)
      }]
    }];
  }
  if (isDataResidencySupported === STORED_IN_ATLASSIAN_N_DR_SUPPORTED) {
    return [{
      type: 'rich-text',
      items: [{
        type: 'text',
        text: 'App stores End-User Data exclusively within Atlassian products and services. See “In-scope product data” for Atlassian products and services '
      }, {
        type: 'link',
        text: 'here.',
        href: 'https://support.atlassian.com/security-and-access-policies/docs/understand-data-residency/'
      }]
    }];
  }
  return null;
};
export const dataResidencyRealmMigration = ({
  dataResidency
}) => {
  return yesNoText(dataResidency.realmMigrationSupported);
};
export const dataRetentionTransform = ({
  dataRetention
}) => {
  const {
    isDataRetentionSupported,
    retentionDurationInDays
  } = dataRetention;
  if (isDataRetentionSupported && retentionDurationInDays) {
    const message = number => "Minimum storage period post un-install: ".concat(number, " days");
    return [{
      type: 'text',
      text: message(retentionDurationInDays.min)
    }, {
      type: 'text',
      text: message(retentionDurationInDays.max)
    }];
  }
  return yesNoText(isDataRetentionSupported);
};
export const customRetentionPeriod = ({
  dataRetention
}) => {
  return yesNoText(dataRetention.isCustomRetentionPeriodAllowed);
};
export const logEndUserData = ({
  logDetails
}) => {
  return yesNoText(logDetails.logEndUserData);
};
export const logEndUserDataOutsideAtlassianOrThirdParty = ({
  logDetails
}) => {
  const {
    logProcessAndOrStoreEUDOutsideAtlassian,
    logEUDShareWithThirdParty
  } = logDetails;
  let isSharedOutsideAtlassianOrThirdParty = logProcessAndOrStoreEUDOutsideAtlassian || logEUDShareWithThirdParty;

  // If both does not exist, it would mean partner has not responded
  if (typeof logProcessAndOrStoreEUDOutsideAtlassian === 'undefined' && typeof logEUDShareWithThirdParty === 'undefined') {
    isSharedOutsideAtlassianOrThirdParty = undefined;
  }
  return yesNoText(isSharedOutsideAtlassianOrThirdParty);
};
export const logIntegralPartOfFunctionality = ({
  logDetails
}) => {
  return yesNoText(logDetails.logEUDShareWithThirdParty);
};
export const complianceStandardsAnswer = ({
  compliantStandards
}) => {
  return compliantStandards !== null && compliantStandards !== void 0 && compliantStandards.length ? [{
    type: 'text',
    text: 'The app is accredited with following compliance standards:'
  }, {
    type: 'link',
    text: 'The app is accredited with following compliance standards:',
    href: 'http://google.com'
  }, {
    type: 'email',
    text: 'foo@example.com',
    mailId: 'foo@example.com'
  }, {
    type: 'bullet-points',
    points: compliantStandards.map(({
      type
    }) => ({
      text: type
    }))
  }, {
    type: 'bullet-points-list',
    bulletedList: [{
      label: 'Culpa sint consequat id deserunt reprehenderit.',
      points: [{
        text: 'Ipsum dolore fugiat et amet ea tempor sit consectetur amet minim ullamco ut adipisicing est.'
      }, {
        text: 'Pariatur officia elit ad reprehenderit minim pariatur duis irure sunt veniam.'
      }, {
        text: 'Cillum dolore esse est consequat nulla nisi in non sint.'
      }, {
        text: 'Occaecat in ullamco aute nulla est ipsum occaecat labore laboris pariatur ipsum excepteur.'
      }]
    }]
  }] : null;
};